import React, { useCallback, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../context/AuthContext';
import { getSchoolAssetUrl, getPlatformAssetUrl } from '../utils/api';
import { isSchoolStaffRole, isPortalRole, needsSubscriptionGate } from '../utils/rolePaths';
import { isCrmRole, canManageCrmEmployees, canManageCrmSalaries, canViewCrmReports, canMarkCrmAttendance, canViewCrmPlans, canManageAssignedFeedback, canAccessCrmTickets, canAccessCrmMail, canManageSchools, canManageBin, canManagePlatformFeedback, canManageSubscriptionPlans, canManageSubscriptions, canAccessCrmInquiries, canAccessCrmDashboard, canManageDepartments, canManageCrmRoles, hasPermission, isSuperAdmin as isCrmSuperAdmin } from '../utils/crmPermissions';
import {
  NAV_GROUPS,
  resolveNavGroups,
  filterPortalNavItems,
  getPortalNavItems,
} from '../utils/navPermissions';
import {
  LayoutDashboard, School, ChevronDown, Mail, Settings,
  Users, IndianRupee, FileText, Building2, ClipboardList, CreditCard, BarChart3, ArrowUpRight, Lock, MessageSquare, Ticket, Inbox, Pin, Trash2, GripVertical, Shield,
} from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import ModuleUpgradeDialog from './subscription/ModuleUpgradeDialog';
import UpgradePromoBanner from './subscription/UpgradePromoBanner';
import { canSubmitFeedback, canManageSchoolFeedback } from '../utils/feedbackPermissions';
import { usePinnedNav } from '../hooks/usePinnedNav';

function crmNavIsActive(pathname, basePath, exact = false) {
  if (exact) {
    return pathname === basePath;
  }
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

function PinButton({ path, pinned, onToggle, navActive }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle(path);
      }}
      className={`ml-auto shrink-0 rounded p-0.5 transition-opacity ${
        pinned
          ? navActive
            ? 'opacity-100 text-white'
            : 'opacity-100 text-amber-500'
          : navActive
            ? 'opacity-100 text-white/80 hover:text-white lg:opacity-0 lg:group-hover:opacity-100'
            : 'opacity-100 text-muted-foreground hover:text-foreground lg:opacity-0 lg:group-hover:opacity-100'
      }`}
      aria-label={pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
      title={pinned ? 'Unpin' : 'Pin'}
      data-testid={`nav-pin-${path.replace(/\//g, '-')}`}
    >
      <Pin className={`w-3.5 h-3.5 ${pinned ? 'fill-current' : ''}`} />
    </button>
  );
}

function SortablePinnedItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-0.5 transition-opacity duration-200 ${isDragging ? 'opacity-60 z-10' : ''}`}
      data-testid={`nav-pinned-item-${String(id).replace(/\//g, '-')}`}
    >
      <button
        type="button"
        className="shrink-0 touch-none rounded p-0.5 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        aria-label="Reorder pinned item"
        title="Drag to reorder"
        data-testid={`nav-pinned-drag-${String(id).replace(/\//g, '-')}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

const Sidebar = ({ isOpen, setIsOpen, schoolBranding = null, platformBranding = null }) => {
  const { user, canOpenSettings, isSchoolObserver: observerMode } = useAuth();
  const { pinnedPaths, isPinned, togglePin, setPinnedPaths } = usePinnedNav(user?.id);
  const pinnedSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const handlePinnedDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = pinnedPaths.indexOf(active.id);
      const newIndex = pinnedPaths.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      setPinnedPaths(arrayMove(pinnedPaths, oldIndex, newIndex));
    },
    [pinnedPaths, setPinnedPaths]
  );
  const isSuperAdmin = user?.role_slug === 'super_admin' && !observerMode;
  const showCrmNav = isCrmRole(user) && !observerMode;
  const resolvedSchoolId = schoolBranding?.id ?? user?.school_id;
  const hasSchoolBrand = Boolean(schoolBranding && resolvedSchoolId);
  const showSchoolBrand = hasSchoolBrand && (!isSuperAdmin || observerMode);
  const expectsSchoolBrand = (!isSuperAdmin && Boolean(user?.school_id)) || observerMode;
  const schoolBrandLoading = expectsSchoolBrand && !schoolBranding;
  const showPlatformBranding = (isSuperAdmin || !user?.school_id) && !observerMode;
  const platformLogoUrl = showPlatformBranding
    ? getPlatformAssetUrl(platformBranding?.logo, platformBranding?.version)
    : null;
  const defaultProductLogo = `${process.env.PUBLIC_URL || ''}/images/school-analytics-product-logo.png`;
  const platformName = platformBranding?.name?.trim() || '';
  const hasCustomPlatformLogo = Boolean(platformLogoUrl);
  const useCompactPlatformBrand = showPlatformBranding && Boolean(platformName && hasCustomPlatformLogo);
  const platformLogoAlt = platformName || 'School Analytics CPRawat — Smart insights. Better education.';
  const location = useLocation();
  const showStaffModules = isSchoolStaffRole(user?.role_slug) || observerMode;
  const showPortalModules = isPortalRole(user?.role_slug);
  const showSettings = canOpenSettings();
  const showSubscriptionUpgrade = user?.role_slug === 'school_admin' && needsSubscriptionGate(user);
  const dashboardPath = user?.role_slug === 'receptionist' ? '/reception' : '/dashboard';
  const showFeedback = canSubmitFeedback(user);
  const showSchoolFeedback = canManageSchoolFeedback(user);

  const staffNavGroups = showStaffModules ? resolveNavGroups(user, NAV_GROUPS) : [];
  const portalNavItems = showPortalModules ? filterPortalNavItems(user, getPortalNavItems(user)) : [];

  const [openGroups, setOpenGroups] = useState({
    academics: true,
    admission: true,
    study: true,
    hr: true,
    fees: true,
    reports: true,
  });
  const [upgradeDialog, setUpgradeDialog] = useState({ open: false, moduleKey: null, moduleLabel: null });

  const openUpgradeDialog = (item) => {
    setUpgradeDialog({
      open: true,
      moduleKey: item.moduleKey || item.path,
      moduleLabel: item.label,
    });
    setIsOpen(false);
  };

  const navCatalog = useMemo(() => {
    const items = [];

    if (showCrmNav) {
      if (canAccessCrmDashboard(user)) {
        items.push({ path: '/crm/dashboard', label: 'CRM Dashboard', icon: LayoutDashboard, end: true, size: 'md' });
      }
      if (canManageCrmEmployees(user)) {
        items.push({ path: '/crm/employees', label: 'Employees', icon: Users, size: 'md', crmActive: true });
      }
      if (canManageDepartments(user) || isCrmSuperAdmin(user)) {
        items.push({ path: '/crm/departments', label: 'Departments', icon: Building2, size: 'md', crmActive: true });
      }
      if (canManageCrmRoles(user)) {
        items.push({ path: '/crm/roles', label: 'Roles', icon: Shield, size: 'md', crmActive: true });
      }
      if (canMarkCrmAttendance(user) || hasPermission(user, 'attendance', 'read')) {
        items.push({ path: '/crm/attendance', label: 'Attendance', icon: ClipboardList, size: 'md', crmActive: true });
      }
      if (canAccessCrmInquiries(user)) {
        items.push({ path: '/crm/inquiries', label: 'Inquiries', icon: Mail, size: 'md', crmActive: true });
      }
      if (canAccessCrmMail(user)) {
        items.push({ path: '/crm/mail', label: 'Mail', icon: Inbox, size: 'md', crmActive: true });
      }
      if (canAccessCrmTickets(user)) {
        items.push({ path: '/crm/tickets', label: 'Tickets', icon: Ticket, size: 'md', crmActive: true });
      }
      if (canManageAssignedFeedback(user)) {
        items.push({ path: '/crm/feedback', label: 'Feedback Management', icon: MessageSquare, size: 'md', crmActive: true });
      }
      if (canViewCrmPlans(user) && !canManageSubscriptionPlans(user)) {
        items.push({ path: '/crm/plans', label: 'Pricing Plans', icon: CreditCard, size: 'md', crmActive: true });
      }
      if (canManageSchools(user)) {
        items.push({ path: '/schools', label: 'Schools', icon: Building2, size: 'md' });
      }
      if (canManageBin(user)) {
        items.push({ path: '/bin', label: 'Bin', icon: Trash2, size: 'md' });
      }
      if (canManagePlatformFeedback(user)) {
        items.push({ path: '/feedback', label: 'Feedback Management', icon: MessageSquare, size: 'md', crmActive: true });
      }
      if (canManageSubscriptionPlans(user)) {
        items.push({ path: '/subscription/plans', label: 'Pricing Plans', icon: CreditCard, size: 'md', crmActive: true });
      }
      if (canManageSubscriptions(user)) {
        items.push({ path: '/subscription/analytics', label: 'Subscriptions', icon: BarChart3, size: 'md' });
      }
      if (canManageCrmSalaries(user) || showCrmNav) {
        items.push({ path: '/crm/salaries', label: 'Payroll', icon: IndianRupee, size: 'md', crmActive: true });
      }
      if (canViewCrmReports(user)) {
        items.push({ path: '/crm/reports', label: 'Reports', icon: FileText, size: 'md', crmActive: true });
      }
    }

    if (!showCrmNav && !isSuperAdmin && showSubscriptionUpgrade) {
      items.push({
        path: '/settings/subscription/upgrade',
        label: 'Choose plan',
        icon: ArrowUpRight,
        size: 'md',
        choosePlan: true,
      });
    }

    if (!showCrmNav && !isSuperAdmin) {
      items.push({
        path: dashboardPath,
        label: 'Dashboard',
        icon: LayoutDashboard,
        size: 'md',
        end: dashboardPath === '/reception',
      });
    }

    portalNavItems.forEach((item) => {
      if (!item.locked) items.push({ ...item, size: 'sm' });
    });

    staffNavGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (!item.locked) items.push({ ...item, size: 'sm' });
      });
    });

    if (showFeedback) {
      if (showSchoolFeedback) {
        items.push({
          path: '/school-feedback',
          label: 'Feedback Management',
          icon: MessageSquare,
          size: 'md',
          pathPrefix: '/school-feedback',
        });
      }
      items.push({ path: '/my-feedback', label: 'My Feedback', icon: MessageSquare, size: 'md' });
    }

    if (showSettings) {
      items.push({ path: '/settings', label: 'Settings', icon: Settings, size: 'md' });
    }

    return items;
  }, [
    user,
    showCrmNav,
    isSuperAdmin,
    showSubscriptionUpgrade,
    dashboardPath,
    portalNavItems,
    staffNavGroups,
    showFeedback,
    showSchoolFeedback,
    showSettings,
  ]);

  const catalogByPath = useMemo(() => {
    const map = new Map();
    navCatalog.forEach((item) => {
      if (!map.has(item.path)) map.set(item.path, item);
    });
    return map;
  }, [navCatalog]);

  const pinnedItems = useMemo(
    () => pinnedPaths.map((path) => catalogByPath.get(path)).filter(Boolean),
    [pinnedPaths, catalogByPath]
  );

  const showInMain = (path) => !isPinned(path);

  const crmItems = useMemo(
    () => navCatalog.filter((item) => {
      if (!showCrmNav) return false;
      if (!showInMain(item.path)) return false;
      return (
        item.path.startsWith('/crm/')
        || item.path === '/schools'
        || item.path === '/bin'
        || item.path === '/feedback'
        || item.path.startsWith('/subscription/')
      );
    }),
    [navCatalog, showCrmNav, pinnedPaths]
  );

  const renderPin = (path, navActive) => (
    <PinButton path={path} pinned={isPinned(path)} onToggle={togglePin} navActive={navActive} />
  );

  const renderNavLink = (item) => {
    const ItemIcon = item.icon;

    if (item.locked) {
      return (
        <TooltipProvider key={item.path} delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => openUpgradeDialog(item)}
                className="group relative flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm opacity-60 cursor-not-allowed text-muted-foreground hover:bg-accent/40"
                data-testid={`nav-locked-${item.path.replace(/\//g, '-')}`}
              >
                <ItemIcon className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left truncate">{item.label}</span>
                <Lock className="w-3.5 h-3.5 shrink-0" aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent>Upgrade your plan to unlock this feature.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.end}
        onClick={() => setIsOpen(false)}
        className={({ isActive }) =>
          `group relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
            isActive
              ? 'text-white bg-blue-600'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent/70'
          }`
        }
        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
      >
        {({ isActive }) => (
          <>
            <ItemIcon className="w-4 h-4 shrink-0" />
            <span className="flex-1 truncate">{item.label}</span>
            {renderPin(item.path, isActive)}
          </>
        )}
      </NavLink>
    );
  };

  const isPrimaryNavActive = (item) => {
    const pathname = location.pathname;
    if (item.choosePlan) {
      return pathname === item.path || pathname.startsWith('/settings/subscription');
    }
    if (item.pathPrefix) {
      return pathname.startsWith(item.pathPrefix);
    }
    if (item.crmActive) {
      return crmNavIsActive(pathname, item.path);
    }
    if (item.end) {
      return pathname === item.path;
    }
    return pathname === item.path || pathname.startsWith(`${item.path}/`);
  };

  const renderPrimaryNavLink = (item) => {
    const ItemIcon = item.icon;
    const navActive = isPrimaryNavActive(item);
    const testId = item.path === '/settings/subscription/upgrade'
      ? 'nav-choose-plan'
      : item.path === dashboardPath
        ? 'nav-dashboard'
        : item.path === '/settings'
          ? 'nav-settings'
          : item.path === '/my-feedback'
            ? 'nav-my-feedback'
            : item.path === '/school-feedback'
              ? 'nav-school-feedback'
              : `nav-${item.path.replace(/\//g, '-')}`;

    return (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.end}
        onClick={() => setIsOpen(false)}
        aria-current={navActive ? 'page' : undefined}
        className={`group relative flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
          navActive
            ? 'bg-blue-600 text-white'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground'
        }`}
        data-testid={testId}
      >
        <ItemIcon className="w-5 h-5 shrink-0" />
        <span className="font-medium flex-1 truncate">{item.label}</span>
        {renderPin(item.path, navActive)}
      </NavLink>
    );
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-card border-r border-border z-50 transform transition-transform duration-300 pb-safe lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="px-3 py-4 border-b border-border">
            {showSchoolBrand ? (
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-10 w-10 rounded-lg border border-border bg-muted shrink-0 overflow-hidden flex items-center justify-center">
                  {schoolBranding.logo ? (
                    <img
                      src={getSchoolAssetUrl(schoolBranding.logo, resolvedSchoolId)}
                      alt=""
                      className="h-full w-full object-contain p-0.5"
                    />
                  ) : (
                    <School className="h-5 w-5 text-muted-foreground" aria-hidden />
                  )}
                </div>
                <span className="font-semibold text-sm text-foreground truncate" title={schoolBranding.name}>
                  {schoolBranding.name}
                </span>
              </div>
            ) : schoolBrandLoading ? (
              <div className="flex items-center gap-2 min-w-0" aria-busy="true" aria-label="Loading school branding">
                <div className="h-10 w-10 rounded-lg border border-border bg-muted shrink-0 flex items-center justify-center">
                  <School className="h-5 w-5 text-muted-foreground" aria-hidden />
                </div>
                <div className="h-4 flex-1 max-w-[8rem] rounded bg-muted animate-pulse" />
              </div>
            ) : showPlatformBranding && useCompactPlatformBrand ? (
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-10 w-10 rounded-lg border border-border bg-muted shrink-0 overflow-hidden flex items-center justify-center">
                  <img
                    src={platformLogoUrl}
                    alt=""
                    className="h-full w-full object-contain p-0.5"
                  />
                </div>
                <span className="font-semibold text-sm text-foreground truncate" title={platformName}>
                  {platformName}
                </span>
              </div>
            ) : showPlatformBranding && hasCustomPlatformLogo ? (
              <img
                src={platformLogoUrl}
                alt={platformLogoAlt}
                className="w-full h-14 object-contain object-left rounded-[5px]"
              />
            ) : showPlatformBranding ? (
              <img
                src={defaultProductLogo}
                alt={platformLogoAlt}
                className="w-full h-14 object-contain object-left rounded-[5px]"
              />
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-10 w-10 rounded-lg border border-border bg-muted shrink-0 flex items-center justify-center">
                  <School className="h-5 w-5 text-muted-foreground" aria-hidden />
                </div>
              </div>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3 flex flex-col min-h-0">
            {pinnedItems.length > 0 && (
              <ul className="space-y-1 mb-3 pb-3 border-b border-border shrink-0" data-testid="nav-pinned-section">
                <li className="px-3 pb-1">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pinned</span>
                </li>
                <DndContext
                  sensors={pinnedSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handlePinnedDragEnd}
                >
                  <SortableContext
                    items={pinnedItems.map((item) => item.path)}
                    strategy={verticalListSortingStrategy}
                  >
                    {pinnedItems.map((item) => (
                      <SortablePinnedItem key={`pinned-${item.path}`} id={item.path}>
                        {item.size === 'md' ? renderPrimaryNavLink(item) : renderNavLink(item)}
                      </SortablePinnedItem>
                    ))}
                  </SortableContext>
                </DndContext>
              </ul>
            )}

            <ul className="space-y-1 flex-1">
              {showCrmNav && crmItems.map((item) => (
                <li key={item.path} className="transition-opacity duration-200">{renderPrimaryNavLink(item)}</li>
              ))}

              {!showCrmNav && !isSuperAdmin && showSubscriptionUpgrade && showInMain('/settings/subscription/upgrade') && (
                <li className="transition-opacity duration-200">
                  {renderPrimaryNavLink({
                    path: '/settings/subscription/upgrade',
                    label: 'Choose plan',
                    icon: ArrowUpRight,
                    choosePlan: true,
                  })}
                </li>
              )}

              {!showCrmNav && !isSuperAdmin && showInMain(dashboardPath) && (
                <li className="transition-opacity duration-200">
                  {renderPrimaryNavLink({
                    path: dashboardPath,
                    label: 'Dashboard',
                    icon: LayoutDashboard,
                    end: dashboardPath === '/reception',
                  })}
                </li>
              )}

              {showPortalModules && portalNavItems.filter((item) => item.locked || showInMain(item.path)).map((item) => (
                <li key={item.path} className="transition-opacity duration-200">{renderNavLink(item)}</li>
              ))}

              {showStaffModules && staffNavGroups.map((group) => {
                const visibleItems = group.items.filter((item) => item.locked || showInMain(item.path));
                if (visibleItems.length === 0) return null;
                const GroupIcon = group.icon;
                const groupOpen = !!openGroups[group.key];
                const isGroupActive = visibleItems.some(
                  (item) =>
                    location?.pathname === item.path ||
                    location?.pathname?.startsWith(`${item.path}/`)
                );
                return (
                  <li key={group.key} className="pt-2 transition-opacity duration-200">
                    <Collapsible open={groupOpen} onOpenChange={(v) => setOpenGroups({ ...openGroups, [group.key]: v })}>
                      <div className="relative">
                        <span
                          className={`absolute left-0 top-0 bottom-0 w-[2px] rounded-full bg-blue-500/70 transition-opacity ${
                            isGroupActive ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className={`relative w-[calc(100%-8px)] ml-2 flex items-center gap-2 pl-1 pr-3 py-2 rounded-lg transition-colors ${
                              isGroupActive ? 'text-foreground bg-accent' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                            }`}
                          >
                            <GroupIcon className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-semibold tracking-wide">{group.label}</span>
                            <ChevronDown className={`ml-auto w-4 h-4 transition-transform ${groupOpen ? 'rotate-180' : ''}`} />
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-1">
                          <div className="pl-3 space-y-1 border-l border-border/70">
                            {visibleItems.map((item) => (
                              <div key={item.path} className="transition-opacity duration-200">
                                {renderNavLink(item)}
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  </li>
                );
              })}
            </ul>

            {showFeedback && (showInMain('/my-feedback') || (showSchoolFeedback && showInMain('/school-feedback'))) && (
              <ul className="space-y-1 pt-3 mt-3 border-t border-border shrink-0">
                {showSchoolFeedback && showInMain('/school-feedback') && (
                  <li className="transition-opacity duration-200">
                    {renderPrimaryNavLink({
                      path: '/school-feedback',
                      label: 'Feedback Management',
                      icon: MessageSquare,
                      pathPrefix: '/school-feedback',
                    })}
                  </li>
                )}
                {showInMain('/my-feedback') && (
                  <li className="transition-opacity duration-200">
                    {renderPrimaryNavLink({
                      path: '/my-feedback',
                      label: 'My Feedback',
                      icon: MessageSquare,
                    })}
                  </li>
                )}
              </ul>
            )}

            {showSettings && (showInMain('/settings') || user?.role_slug === 'school_admin') && (
              <ul className="space-y-1 pt-3 mt-3 border-t border-border shrink-0">
                {user?.role_slug === 'school_admin' && (
                  <li className="px-2 pb-2">
                    <UpgradePromoBanner user={user} compact />
                  </li>
                )}
                {showInMain('/settings') && (
                  <li className="transition-opacity duration-200">
                    {renderPrimaryNavLink({
                      path: '/settings',
                      label: 'Settings',
                      icon: Settings,
                    })}
                  </li>
                )}
              </ul>
            )}
          </nav>
        </div>
      </aside>

      <ModuleUpgradeDialog
        open={upgradeDialog.open}
        moduleKey={upgradeDialog.moduleKey}
        moduleLabel={upgradeDialog.moduleLabel}
        onClose={() => setUpgradeDialog({ open: false, moduleKey: null, moduleLabel: null })}
      />
    </>
  );
};

export default Sidebar;
